import type { FreightMode, SupplierFreightRule } from '../types'

export interface FreightInput {
  supplier: string
  productSubtotal: number
  preferredMode?: FreightMode
}

export interface FreightBreakdown {
  supplier: string
  mode: FreightMode
  amount: number
  freeFreightApplied: boolean
  minOrderMet: boolean
  minOrderValue: number
  shortfall: number
}

/**
 * FreightCalculator — fixed, variable, free threshold, pickup.
 */
export class FreightCalculator {
  private rules: Map<string, SupplierFreightRule>

  constructor(rules: SupplierFreightRule[] = []) {
    this.rules = new Map(rules.map((r) => [r.supplier, r]))
  }

  setRules(rules: SupplierFreightRule[]) {
    this.rules = new Map(rules.map((r) => [r.supplier, r]))
  }

  getRule(supplier: string): SupplierFreightRule {
    return this.rules.get(supplier) ?? defaultRule(supplier)
  }

  calculate(input: FreightInput): FreightBreakdown {
    const rule = this.getRule(input.supplier)
    const mode = input.preferredMode ?? rule.mode
    const minOrderMet = input.productSubtotal >= rule.minOrderValue
    const shortfall = minOrderMet ? 0 : round2(rule.minOrderValue - input.productSubtotal)

    if (mode === 'PICKUP') {
      return {
        supplier: input.supplier,
        mode,
        amount: 0,
        freeFreightApplied: false,
        minOrderMet,
        minOrderValue: rule.minOrderValue,
        shortfall,
      }
    }

    if (mode === 'FREE' || input.productSubtotal >= rule.freeFreightMin) {
      return {
        supplier: input.supplier,
        mode: mode === 'FREE' ? 'FREE' : rule.mode,
        amount: 0,
        freeFreightApplied: true,
        minOrderMet,
        minOrderValue: rule.minOrderValue,
        shortfall,
      }
    }

    if (mode === 'VARIABLE') {
      return {
        supplier: input.supplier,
        mode,
        amount: round2(input.productSubtotal * rule.variableRate),
        freeFreightApplied: false,
        minOrderMet,
        minOrderValue: rule.minOrderValue,
        shortfall,
      }
    }

    return {
      supplier: input.supplier,
      mode: 'FIXED',
      amount: round2(rule.fixedAmount),
      freeFreightApplied: false,
      minOrderMet,
      minOrderValue: rule.minOrderValue,
      shortfall,
    }
  }
}

function defaultRule(supplier: string): SupplierFreightRule {
  const hash = [...supplier].reduce((s, c) => s + c.charCodeAt(0), 0)
  const modes: FreightMode[] = ['FIXED', 'VARIABLE', 'FIXED', 'VARIABLE']
  return {
    supplier,
    mode: modes[hash % modes.length]!,
    fixedAmount: 25 + (hash % 40),
    variableRate: 0.02 + (hash % 5) / 1000,
    freeFreightMin: 800 + (hash % 7) * 100,
    minOrderValue: hash % 3 === 0 ? 150 : 0,
  }
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const freightCalculator = new FreightCalculator()
